import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TaskType } from 'api/tasks';
import {
  TimesheetPhaseReport,
  TimesheetTaskReport,
} from 'api/timesheets/reports/types';
import { ReactComponent as PhaseIcon } from 'assets/svgs/phase-timesheet-settings.svg';
import { StatusIndicatorIcon } from 'components/molecules/status-indicator';
import {
  NameCell,
  NameCellWithTooltip,
} from 'components/organisms/timesheets/common/cells/name-cell';
import { PercentWithTimeCell } from 'components/organisms/timesheets/common/cells/percent-with-time-cell';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { calculatePercentage } from 'components/organisms/timesheets/common/utils';
import {
  billableMinutesSorter,
  nonBillableMinutesSorter,
  phaseNameSorter,
  totalMinutesSorter,
} from './sorter';

export const StyledNameCell = styled(NameCell)`
  svg {
    width: 18px;
    height: 18px;
  }
`;

export const useProjectWisePhaseConfig = ({
  noOfRows,
}: {
  noOfRows: number;
}) => {
  const getTaskField = (fields, fieldName) =>
    fields.find((field) => field.fieldName === fieldName);

  return [
    {
      title: (
        <FormattedMessage
          id="reports.columns.phases"
          values={{ count: noOfRows }}
        />
      ),
      key: 'phase',
      width: '310px',
      render: (_, record: TimesheetPhaseReport) => {
        const { projectPhase } = record;

        if (!projectPhase) {
          return (
            <StyledNameCell
              logo={<PhaseIcon />}
              primaryText={<FormattedMessage id="timesheets.column.no_phase" />}
            />
          );
        }

        return (
          <NameCellWithTooltip
            logo={
              <StatusIndicatorIcon
                group={getTaskField(projectPhase.fields, 'Status')?.fieldValue}
                type={TaskType.Task}
              />
            }
            primaryText={projectPhase.projectPhaseName}
          />
        );
      },
      summaryRender: () => <FormattedMessage id="timesheets.column.total" />,
      defaultSortOrder: 'ascend',
      sorter: phaseNameSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.hours_tracked" />,
      key: 'hoursTracked',
      width: '200px',
      render: (_, record: TimesheetTaskReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        return (
          <BaseCell>
            <FormatTime value={billableMinutes + nonBillableMinutes} />
          </BaseCell>
        );
      },
      summaryRender: (_, summaryRecord: TimesheetTaskReport) => (
        <FormatTime
          value={
            summaryRecord.billableMinutes + summaryRecord.nonBillableMinutes
          }
        />
      ),
      sorter: totalMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.billable_hours" />,
      key: 'billableHours',
      width: '200px',
      render: (_, record: TimesheetTaskReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          billableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={billableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetTaskReport) => (
        <FormatTime value={summaryRecord.billableMinutes} />
      ),
      sorter: billableMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.non_billable_hours" />,
      key: 'nonBillableHours',
      render: (_, record: TimesheetTaskReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          nonBillableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={nonBillableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetTaskReport) => (
        <FormatTime value={summaryRecord.nonBillableMinutes} />
      ),
      sorter: nonBillableMinutesSorter,
      showSorterTooltip: false,
    },
  ];
};
